import { useEffect, useRef, useState } from 'react';
import Hand from './Hand';
import ActiveCard from './ActiveCard';
import OtherPlayer from './OtherPlayer';
import ColorPickModal from './modals/ColorPickModal';
import PlayCardModal from './modals/PlayCardModal';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import directionImg from '../assets/direction.svg'
import playCardAudio from '../assets/play_card.mp3';
import playPlusFourAudio from '../assets/play_plusFour.mp3';
import PickSevenTargetModal from './modals/PickSevenTargetModal';
import colorsGray from '../assets/colors_gray_border.svg';
import colors from '../assets/colors.svg';
import StackModal from './modals/StackModal';
import PickRainbowCardModal from './modals/PickRainbowCardModal';
import FlashModal from './modals/FlashModal';

gsap.registerPlugin(useGSAP);

function Game({ socket, players, setGamePlayers, playerId, complete, gameSettings }) {
    const [activePlayerId, setActivePlayerId] = useState(null);
    const [activeCard, setActiveCard] = useState(null);
    const [hand, setHand] = useState(null);
    const [showColorModal, setShowColorModal] = useState(false);
    const [promptPlayCard, setPromptPlayCard] = useState(null);
    const [direction, setDirection] = useState(1);
    const directionRef = useRef(direction);
    const [promptPickSevenTarget, setPromptPickSevenTarget] = useState(false);
    const [animation, setAnimation] = useState(null);
    const cardRef = useRef();
    const [postAnimation, setPostAnimation] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [stackTwoAmount, setStackTwoAmount] = useState(null);
    const [stackFourAmount, setStackFourAmount] = useState(null);
    const [promptPickRainbowCard, setPromptRainbowCard] = useState(false);
    const [flash, setFlash] = useState(false);

    const isMyTurn = () => {
        return activePlayerId === playerId && !animation && !complete;
    }

    const isValidPlay = (card) => {
        // check if content is the same as active card
        const contentMatch = card.content === activeCard.content;
        // check if the card is of same color
        const colorMatch = card.color === activeCard.color;
        // check if wild card
        const isWild = card.color === 'wild' || card.content === 'plusFour_colorPicked' || card.content === 'colorChange_colorPicked'; // last two conditions for if color is picked
        // check if rainbow card
        const isRainbow = card.color === 'rainbow' || activeCard.color === 'rainbow';

        // no wild cards on top of rainbow cards
        if (activeCard.color === 'rainbow' && isWild) {
            return false;
        }

        return contentMatch || colorMatch || isWild || isRainbow;
    }

    const drawCard = () => {
        // is it the players turn?
        if (!isMyTurn()) return;

        if (isDrawing) return;
        setIsDrawing(true);

        //emit to the server that I want to draw a card
        socket.emit('draw_card', playerId);
    }

    const playCard = (card) => {
        // check if no animaton is being played
        if (animation !== null) return;

        // is it the players turn?
        if (!isMyTurn()) return;

        // is it a valid play?
        if (!isValidPlay(card)) return;

        // remove the play card modal
        setPromptPlayCard(null);

        // emit to play this card
        socket.emit('play_card', card);

        // reset isDrawing
        setIsDrawing(false);
    }

    const passTurn = () => {
        setPromptPlayCard(null);
        socket.emit('pass_turn');
    }

    const onSelectColor = (color) => {
        // console.log('player picked color', color);
        setShowColorModal(false);
        setStackFourAmount(null);

        // make a new card based on the color picked and the one laying there
        const colorCard = {
            color: color, // eg yellow, red, blue, green
            content: activeCard.content + '_colorPicked', // eg '_colorPicked', 'plusFour_colorPicked'
            amount: stackFourAmount,
        }

        playCard(colorCard);
    }

    const onPickSevenTarget = (targetId) => {
        setPromptPickSevenTarget(false);

        // emit to the server to switch hands with target
        socket.emit('picked_seven_target', targetId);
    }

    const onStack = (card) => {
        setStackTwoAmount(null);
        setStackFourAmount(null);

        if (stackTwoAmount) {
            card = {...card, amount: stackTwoAmount + 2};
        } else if (stackFourAmount) {
            card = {...card, amount: stackFourAmount + 4};
        }

        playCard(card);
    }

    const onTank = () => {
        setStackTwoAmount(null);
        setStackFourAmount(null);

        // tell server to skip my turn and to add amount of cards to my hand
        if (stackTwoAmount) {
            socket.emit('tank', stackTwoAmount)  
        } else if (stackFourAmount) {
            socket.emit('tank', stackFourAmount)  
        }
    }

    const onRainbowify = (card) => {
        socket.emit('picked_rainbow_card', card);
    }

    const directionClass = direction === 1 ? 'scale-x-[-1]' : ''
    const cardPlayAudio = new Audio(playCardAudio);
    const plusFourPlayAudio = new Audio(playPlusFourAudio);

    const reorderedPlayers = [...players];
    if (playerId && reorderedPlayers.some(player => player.id === playerId)) {
        while (reorderedPlayers[0]?.id !== playerId) {
            const firstInArray = reorderedPlayers.shift();
            reorderedPlayers.push(firstInArray);
        }
    } else {
        console.warn('player id not found in players array');
    }

    const onAnimationComplete = (animation) => {
        if (animation.hand) {
            setHand(animation.hand);
        }

        if (animation.name !== null) {
            setAnimation({
                name: animation.name
            });
        } else {
            setAnimation(null);
        }

        if (animation.effect) {
            setPostAnimation(animation.effect);
        }
    }

    const onPlayerHandAnimationComplete = (pId) => {
        // console.log('setting animation null for player: ', pId)
        setGamePlayers(prevPlayers => 
            prevPlayers.map(player => {
                if (player.id === pId) {
                    return {
                        ...player,
                        animation: null,
                    }
                }
                return player;
            })
        );
        // console.log(players)
    }

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key.toLowerCase() === 'd') {
                drawCard()
            }
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [drawCard]);

    useEffect(() => {
        switch (postAnimation) {
            case 'draw':
                if (gameSettings.isDrawToMatch) {
                    drawCard();
                    setPostAnimation(null);
                }
                break;
            default:
                break;
        }
    }, [postAnimation]);
    
    useEffect(() => {
        const handleGetStartingInfo = (activePlayerId, activeCard, hand) => {
            setActivePlayerId(activePlayerId);
            setActiveCard(activeCard);
            setHand(hand);
        }

        const handleUpdatePlayerHandSize = (pId, hand, animation) => {
            if (playerId === pId) {
                // ('dont need to update own hand');
                return;
            }
            
            let anim;
            if (animation === 'swap') {
                anim = 'fadeDown'
            } else if (animation === 'cycle') {
                const way = directionRef.current === -1 ? 'left' : 'right' // other direction since other players top of screen
                anim = `cycle-${way}-out`
            } else {
                anim = null;
            }

            setGamePlayers(prevPlayers => 
                prevPlayers.map(player => {
                    if (player.id === pId) {
                        return {
                            ...player,
                            previousHand: player.hand,
                            hand: hand,
                            animation: anim,
                        }
                    } 
                    return {
                        ...player,
                        // previousHand: null,
                        // animation: null,
                    };
                })
            );
        }

        const handleUpdateHand = (hand, animation) => {
            // hide the rainbow modal
            setPromptRainbowCard(false);
            // console.log('update hand animation:', animation)
            switch (animation) {
                case 'swap': // 7 was played
                    setAnimation({ name: 'fadeUp', hand: hand })
                    break;
                case 'cycle': // 0 was played
                    const way = directionRef.current === -1 ? 'right' : 'left'
                    setAnimation({ name: `cycle-${way}-out`, hand: hand })
                    break;
                case 'draw':
                    setHand(hand);
                    setIsDrawing(false);
                    setAnimation({ name: 'draw' });
                    break;
                default:
                    setHand(hand)
                    break;
            }
        }

        const handlePassTurn = (playerId) => {
            setIsDrawing(false);
            setActivePlayerId(playerId);
        }

        const handleUpdateActiveCard = (card) => {
            setActiveCard(card);
        }

        const handlePickColor = (amount) => {
            setShowColorModal(true);
            setStackFourAmount(amount);
        }

        const handlePromptPlayCard = (card) => {
            setPromptPlayCard(card);
        }

        const handleChangeDirection = (direction) => {
            setDirection(direction);
        }

        const handlePickSevenTarget = () => {
            setPromptPickSevenTarget(true);
        }

        const handleStackPlusTwo = (amount) => {
            setStackTwoAmount(amount);
        }

        const handleStackPlusFour = (amount) => {
            setStackFourAmount(amount);
        }

        const handlePickRainbowCard = () => {
            setPromptRainbowCard(true);
        }

        const handleFlash = () => {
            setFlash(true);
        }

        socket.on('get_starting_info', handleGetStartingInfo);
        socket.on('update_player_hand_size', handleUpdatePlayerHandSize);
        socket.on('update_hand', handleUpdateHand);
        socket.on('pass_turn', handlePassTurn);
        socket.on('update_active_card', handleUpdateActiveCard);
        socket.on('pick_color', handlePickColor);
        socket.on('prompt_play_card', handlePromptPlayCard);
        socket.on('change_direction', handleChangeDirection);
        socket.on('pick_seven_target', handlePickSevenTarget);
        socket.on('stack_plus_two', handleStackPlusTwo);
        socket.on('stack_plus_four', handleStackPlusFour);
        socket.on('pick_rainbow_card', handlePickRainbowCard);
        socket.on('flash', handleFlash);

        // ask for the active player and card
        socket.emit('get_starting_info');
    
        return () => {
          socket.off('get_starting_info', handleGetStartingInfo);
          socket.off('update_player_hand_size', handleUpdatePlayerHandSize);
          socket.off('update_hand', handleUpdateHand);
          socket.off('pass_turn', handlePassTurn);
          socket.off('update_active_card', handleUpdateActiveCard);
          socket.off('pick_color', handlePickColor);
          socket.off('prompt_play_card', handlePromptPlayCard);
          socket.off('change_direction', handleChangeDirection);
          socket.off('pick_seven_target', handlePickSevenTarget);
          socket.off('stack_plus_two', handleStackPlusTwo);
          socket.off('stack_plus_four', handleStackPlusFour);
          socket.off('pick_rainbow_card', handlePickRainbowCard);
          socket.off('flash', handleFlash);
        };
    }, [socket]);

    useEffect(() => {
        directionRef.current = direction;
    }, [direction]);

    useGSAP(() => {
        if (activeCard?.content !== 'colorChange_colorPicked' && activeCard?.content !== 'plusFour_colorPicked') {
            if (activeCard?.content === 'plusFour') {
                plusFourPlayAudio.play();
            } else {
                cardPlayAudio.play();
            }
            gsap.from(cardRef.current, { y: -15, opacity: 0.75, ease: 'power2.out', duration: 0.8 });
        }
    }, { dependencies: [activeCard], revertOnUpdate: true });

    return (
        <div className='h-screen w-screen flex flex-col items-center'>
            <div className='flex justify-center shrink-0 overflow-y-hidden overflow-x-auto scrollbar mt-4 pb-2 w-[90%]'>
                <div className='flex justify-center gap-3 w-full'>
                    { reorderedPlayers && reorderedPlayers.filter(p => p.id !== playerId).map(player => {
                        return (
                            <OtherPlayer key={player.id} player={player} activePlayerId={activePlayerId} onAnimationComplete={onPlayerHandAnimationComplete} />
                        );
                    })}
                </div>
            </div>
            { activeCard && direction && (
                <div className='flex-grow relative flex justify-center items-center'>
                    <ActiveCard card={activeCard} ref={cardRef} />
                    <div className='flex flex-col h-[192px] pb-4 gap-4 justify-end absolute -right-20'>
                        <img className={'w-10 transition ' + directionClass} src={directionImg} alt='direction' />
                        <DrawButton onClick={drawCard} active={isMyTurn() && !isDrawing}/>
                    </div>
                </div>
            )}
            { hand && (
                <div className='mt-4 mb-2 w-full'>
                    <div className='w-full flex justify-center'>
                        <Hand hand={hand} playCard={playCard} isActive={isMyTurn()} animation={animation} onAnimationComplete={onAnimationComplete} />
                    </div>
                </div>
            )}
            {showColorModal && !complete && (
                <ColorPickModal onSelectColor={onSelectColor} />
            )}
            {promptPlayCard && !complete && (
                <PlayCardModal card={promptPlayCard} onPlay={() => playCard(promptPlayCard)} onPass={passTurn} />
            )}
            {promptPickSevenTarget && !complete && (
                <PickSevenTargetModal players={players.filter(p => p.id !== playerId)} onSelectTarget={onPickSevenTarget} />
            )}
            {stackTwoAmount && !complete && (
                <StackModal cards={hand.filter(card => card.content === 'plusTwo')} amount={stackTwoAmount} onPlay={onStack} onTank={onTank} />
            )}
            {!showColorModal && stackFourAmount && !complete && (
                <StackModal cards={hand.filter(card => card.content === 'plusFour')} amount={stackFourAmount} onPlay={onStack} onTank={onTank} />
            )}
            {promptPickRainbowCard && !complete && (
                <PickRainbowCardModal cards={hand.filter(card => !['wild', 'rainbow'].includes(card.color))} onPick={onRainbowify} />
            )}
            {flash && (
                <FlashModal onComplete={() => {setFlash(false)}} />
            )}
        </div>
    )
}

function DrawButton({ onClick, active=false }) {
    return (
        <button onClick={onClick} className={`h-12 w-10 flex justify-center items-center rounded border-2 bg-neutral-800 ${active ? 'border-neutral-300 hover:bg-neutral-900' : 'cursor-default border-neutral-500'}`}>
            <img src={active ? colors : colorsGray} className='w-5' alt='Draw card'/>
        </button>
    )
}

export default Game;